import React, { useState, useEffect, useMemo } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { 
    FaEye, 
    FaSearch, 
    FaClinicMedical, 
    FaStore,
    FaSort,
    FaSortUp,
    FaSortDown,
    FaFilter,
    FaPrint
} from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { fetchPharmacies, clearError, clearSuccess } from '../../../redux/Slices/AdminPharmaciesSlice';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';
import PrintInvoicesModal from '../../../shared/components/PrintInvoicesModal';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const columnHelper = createColumnHelper();

const AdminPharmacies = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const { pharmacies, pagination, loading, error, success } = useSelector((state) => state.adminPharmacies);
    
    const [currentPage, setCurrentPage] = useState(1);
    const [searchName, setSearchName] = useState('');
    const [printModal, setPrintModal] = useState({ isOpen: false, storeId: null });
    
    // Table states
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        setTitle('إدارة الصيدليات');
    }, [setTitle]);

    useEffect(() => {
        dispatch(fetchPharmacies({ 
            page: currentPage, 
            name: searchName
        }));
    }, [dispatch, currentPage, searchName]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (success) {
            showSuccessToast(success);
            dispatch(clearSuccess());
            dispatch(fetchPharmacies({ 
                page: currentPage, 
                name: searchName
            }));
        }
    }, [success, dispatch, currentPage, searchName]);

    const handleViewDetails = (pharmacyId) => {
        navigate(`/admin-pharmacies/${pharmacyId}`);
    };

    const handleViewBranches = (pharmacyId) => {
        navigate(`/admin-pharmacies/${pharmacyId}/branches`);
    };

    const handleSearch = () => {
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchName('');
        setCurrentPage(1);
    };

    const getStatusBadge = (status) => {
        if (status === "1" || status === 1) {
            return <span className="badge bg-success text-white">نشط</span>;
        }
        return <span className="badge bg-danger text-white">غير نشط</span>;
    };

    // تعريف الأعمدة
    const columns = useMemo(
        () => [
            columnHelper.accessor('id', {
                id: 'id',
                header: '#',
                cell: ({ row }) => (
                    <div className="order-number-badge">
                        #{(currentPage - 1) * 10 + row.index + 1}
                    </div>
                ),
                size: 60,
                minSize: 60,
                maxSize: 60,
            }),
            columnHelper.accessor('market_name', {
                id: 'name',
                header: 'اسم الصيدلية',
                cell: ({ row }) => (
                                                        <div className="pharmacy-name">
                        {row.original.market_name || 'غير محدد'}
                                                        </div>
                ),
                size: 200,
                minSize: 150,
                maxSize: 250,
            }),
            columnHelper.accessor('phone', {
                id: 'phone',
                header: 'رقم الهاتف',
                cell: ({ row }) => (
                                                        <div className="phone-badge">
                        {row.original.phone || 'غير محدد'}
                                                        </div>
                ),
                size: 150,
                minSize: 120,
                maxSize: 180,
            }),
            columnHelper.accessor('email', {
                id: 'email',
                header: 'البريد الإلكتروني',
                cell: ({ row }) => (
                                                        <div className="email-display-full">
                        {row.original.email || 'غير محدد'}
                                                        </div>
                ),
                size: 400,
                minSize: 350,
                maxSize: 500,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'حالة النشاط',
                cell: ({ row }) => getStatusBadge(row.original.status),
                size: 120,
                minSize: 100,
                maxSize: 140,
            }),
            columnHelper.accessor('created_at', {
                id: 'date',
                header: 'تاريخ التسجيل',
                cell: ({ row }) => (
                                                        <div className="date-display">
                        {row.original.created_at ? new Date(row.original.created_at).toLocaleDateString('ar-EG') : 'غير محدد'}
                                                        </div>
                ),
                size: 120,
                minSize: 100,
                maxSize: 140,
            }),
            columnHelper.display({
                id: 'branches',
                header: 'الفروع',
                cell: ({ row }) => (
                    <div className="action-buttons">
                                                        <motion.button
                                                            whileHover={{ scale: 1.1, y: -2 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className="btn btn-branches"
                            onClick={() => handleViewBranches(row.original.id)}
                                                            title="عرض الفروع">
                                                            <FaStore />
                                                        </motion.button>
                    </div>
                ),
                size: 80,
                minSize: 80,
                maxSize: 80,
            }),
            columnHelper.display({
                id: 'actions',
                header: 'الإجراءات',
                cell: ({ row }) => {
                    const pharmacy = row.original;
                    return (
                                                        <div className="action-buttons">
                                                            <motion.button
                                                            whileHover={{ scale: 1.1, y: -2 }}
                                                            whileTap={{ scale: 0.95 }}
                                                                className="btn btn-view"
                                                                onClick={() => handleViewDetails(pharmacy.id)}
                                                                title="عرض التفاصيل">
                                                                <FaEye />
                                                            </motion.button>
                                                            <motion.button
                                                                whileHover={{ scale: 1.05, y: -1 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-print"
                                                                onClick={() => setPrintModal({ isOpen: true, storeId: pharmacy.id })}
                                                                title="طباعة فواتير"
                                                                style={{ fontSize: '1.1rem' }}>
                                                                <FaPrint />
                                                            </motion.button>
                                                        </div>
                    );
                },
                size: 100,
                minSize: 80,
                maxSize: 120,
            }),
        ],
        [currentPage]
    );

    // إعداد الجدول
    const table = useReactTable({
        data: pharmacies || [],
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });


    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">إدارة الصيدليات</h4>
                </div>

                {/* Search and Filter Controls */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern mb-4"
                >
                    <div className="card-body p-3">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="search-container position-relative">
                                    <div className="input-group search-input-group">
                                        <span className="input-group-text search-icon">
                                            <FaSearch className="text-white" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control search-input"
                                            placeholder="البحث في الصيدليات..."
                                            value={globalFilter ?? ''}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 text-end">
                                <div className="d-flex gap-2 justify-content-end align-items-center">
                                    {(globalFilter || columnFilters.length > 0) && (
                                        <span className="badge bg-info text-white me-2">
                                            {table.getFilteredRowModel().rows.length} من {pharmacies?.length || 0} نتيجة
                                        </span>
                                    )}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowFilters(!showFilters)}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <FaFilter className="mx-1" />
                                        {showFilters ? 'إخفاء المرشحات' : 'إظهار المرشحات'}
                                    </button>
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                            setGlobalFilter('');
                                            setColumnFilters([]);
                                            setSorting([]);
                                        }}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        مسح الكل
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        {/* Advanced Filters */}
                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-3 pt-3 border-top"
                            >
                                <div className="row">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">حالة النشاط</label>
                                        <select
                                            className="form-select"
                                            value={columnFilters.find(f => f.id === 'status')?.value ?? ''}
                                            onChange={(e) => {
                                                const statusFilter = e.target.value;
                                                setColumnFilters(prev => 
                                                    prev.filter(f => f.id !== 'status').concat(
                                                        statusFilter ? [{ id: 'status', value: statusFilter }] : []
                                                    )
                                                );
                                            }}
                                        >
                                            <option value="">جميع الحالات</option>
                                            <option value="1">نشط</option>
                                            <option value="0">غير نشط</option>
                                        </select>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </motion.div>

                {/* Modern Data Table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern"
                >
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل الصيدليات...</p>
                            </div>
                        ) : pharmacies && Array.isArray(pharmacies) && pharmacies.length > 0 ? (
                            <div className="table-responsive" style={{ 
                                overflowX: 'auto', 
                                overflowY: 'visible',
                                maxHeight: 'none'
                            }}>
                                <table className="table mb-0 table-modern" style={{ 
                                    backgroundColor: 'transparent', 
                                    '--bs-table-striped-bg': 'transparent',
                                    '--bs-table-hover-bg': 'rgba(0,0,0,0.05)',
                                    minWidth: '1200px',
                                    width: '100%'
                                }}>
                                    <thead className="table-light">
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(header => (
                                                    <th
                                                        key={header.id}
                                                        className="border-0 fw-semibold text-center"
                                                        style={{ 
                                                            backgroundColor: 'transparent',
                                                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                            userSelect: 'none',
                                                            width: header.getSize()
                                                        }}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                            {header.column.getCanSort() && (
                                                                <span>
                                                                    {header.column.getIsSorted() === 'asc' ? (
                                                                        <FaSortUp className="text-primary" />
                                                                    ) : header.column.getIsSorted() === 'desc' ? (
                                                                        <FaSortDown className="text-primary" />
                                                                    ) : (
                                                                        <FaSort className="text-muted" />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                        </tr>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map((row, index) => (
                                            <motion.tr
                                                key={row.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                                style={{ backgroundColor: 'transparent' }}
                                                className="table-row-hover"
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <td
                                                        key={cell.id}
                                                        className="border-0 text-center align-middle"
                                                        style={{ 
                                                            backgroundColor: 'transparent',
                                                            width: cell.column.getSize()
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </motion.tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                        ) : (
                            <div className="text-center py-5">
                                <div className="text-muted">
                                    <FaClinicMedical size={48} className="mb-3" />
                                    <p>لا توجد صيدليات حالياً</p>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Table Pagination */}
                    {table.getPageCount() > 1 && (
                        <div className="card-footer bg-light">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted">عرض</span>
                                        <select
                                            className="form-select form-select-sm"
                                            style={{ width: 'auto' }}
                                            value={table.getState().pagination.pageSize}
                                            onChange={(e) => table.setPageSize(Number(e.target.value))}
                                        >
                                            {[5, 10, 20, 30, 50].map(pageSize => (
                                                <option key={pageSize} value={pageSize}>
                                                    {pageSize}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="text-muted">
                                            من {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} إلى{' '}
                                            {Math.min(
                                                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                                table.getFilteredRowModel().rows.length
                                            )}{' '}
                                            من أصل {table.getFilteredRowModel().rows.length} نتيجة
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex justify-content-end gap-2">
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(0)}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            الأولى
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.previousPage()}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            السابقة
                                        </button>
                                        <span className="d-flex align-items-center px-3">
                                            صفحة {table.getState().pagination.pageIndex + 1} من {table.getPageCount()}
                                        </span>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.nextPage()}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            التالية
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            الأخيرة
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* API Pagination */}
                {pagination && (pagination.total > 0 || pharmacies?.length > 0) && (
                    <div className="card-footer bg-light">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="text-muted">الانتقال إلى الصفحة</span>
                                    <div className="input-group" style={{ width: 'auto' }}>
                                        <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                            #
                                        </span>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={currentPage}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                if (!isNaN(value) && value > 0) {
                                                    const totalPages = pagination?.total_pages || pagination?.last_page || 1;
                                                    if (value <= totalPages) {
                                                        setCurrentPage(value);
                                                    }
                                                }
                                            }}
                                            min="1"
                                            max={pagination?.total_pages || pagination?.last_page || 1}
                                            style={{ width: '60px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2 flex-wrap">
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage <= 1}
                                    >
                                        الأولى
                                    </button>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                    >
                                        السابقة
                                    </button>
                                    <span className="d-flex align-items-center px-3">
                                        صفحة {pagination.current_page || currentPage} من {pagination.total_pages || pagination.last_page || 1}
                                    </span>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        disabled={currentPage >= (pagination.total_pages || pagination.last_page || 1)}
                                    >
                                        التالية
                                    </button>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setCurrentPage(pagination.total_pages || pagination.last_page || 1)}
                                        disabled={currentPage >= (pagination.total_pages || pagination.last_page || 1)}
                                    >
                                        الأخيرة
                                    </button>
                                </div>
                            </div>
                    </div>
                </div>
                )}
            </div>

            <PrintInvoicesModal
                isOpen={printModal.isOpen}
                onClose={() => setPrintModal({ isOpen: false, storeId: null })}
                storeId={printModal.storeId}
                baseUrl={BASE_URL}
            />
        </div>
    );
};

export default AdminPharmacies;

// CSS للجدول المحسن
const styles = `
.btn-toggle:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.table-modern {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    min-width: 1200px;
  }
  
  .table-modern thead th {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    color: #495057;
    padding: 1rem 0.75rem;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .table-modern tbody tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f1f3f4;
  }
  
  .table-modern tbody tr:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.table-modern tbody tr:last-child {
    border-bottom: none;
}

.table-modern td {
    padding: 1rem 0.75rem;
    vertical-align: middle;
    white-space: normal;
    word-wrap: break-word;
    max-width: 200px;
    min-width: 120px;
  }
  
  /* تصميم البريد الإلكتروني الكامل */
  .email-display-full {
    background: #f8fafc;
    color: #64748b;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    word-break: break-all;
    min-width: 300px;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.action-buttons .btn {
    margin: 2px;
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.2s ease;
    min-width: 80px;
}

.action-buttons .btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.card-modern {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    overflow: hidden;
}

.card-modern .card-body {
    padding: 1.5rem;
}

.table-responsive {
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
    max-height: none;
}

/* Custom Horizontal Scrollbar for Table */
.table-responsive::-webkit-scrollbar {
    height: 8px;
    width: 0;
}

.table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border-radius: 4px;
    transition: all 0.3s ease;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%);
    transform: scale(1.1);
}

/* Firefox Horizontal Scrollbar */
.table-responsive {
    scrollbar-width: thin;
    scrollbar-color: #184194 #f1f1f1;
}

/* Search Input Styling */
.search-container {
    position: relative;
}

.search-input-group {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.search-input-group:focus-within {
    box-shadow: 0 4px 16px rgba(24, 65, 148, 0.2);
    transform: translateY(-1px);
}

.search-icon {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border: none;
    color: white;
    padding: 12px 16px;
    font-size: 16px;
}

.search-input {
    border: none;
    padding: 12px 16px;
    font-size: 14px;
    background: white;
    transition: all 0.3s ease;
}

.search-input:focus {
    border: none;
    box-shadow: none;
    background: #f8f9fa;
}

.search-input::placeholder {
    color: #6c757d;
    font-style: italic;
}

.input-group .form-control:focus {
    border-color: #184194;
    box-shadow: 0 0 0 0.2rem rgba(24, 65, 148, 0.25);
}

.btn-outline-secondary:hover {
    background-color: #6c757d;
    border-color: #6c757d;
}

.btn-outline-primary:hover {
    background-color: #184194;
    border-color: #184194;
}

.table-row-hover:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
}

/* Original Pagination Styling */
.pagination {
    margin-bottom: 0;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.25rem;
}

.pagination .page-item {
    margin: 0.125rem;
}

.pagination .page-link {
    color: #184194;
    border-color: #dee2e6;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    margin: 0 2px;
    transition: all 0.2s ease;
}

.pagination .page-link:hover {
    color: #fff;
    background-color: #184194;
    border-color: #184194;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(24, 65, 148, 0.3);
}

.pagination .page-item.active .page-link {
    background-color: #184194;
    border-color: #184194;
    color: white;
}

.pagination .page-item.disabled .page-link {
    color: #6c757d;
    background-color: #fff;
    border-color: #dee2e6;
    cursor: not-allowed;
}

@media (max-width: 768px) {
    .action-buttons {
        flex-direction: column;
        gap: 4px;
    }
    
    .action-buttons .btn {
        width: 100%;
        font-size: 11px;
        padding: 4px 6px;
    }
    
    .table-modern {
        min-width: 800px;
    }
    
    .table-modern th,
    .table-modern td {
        padding: 0.5rem 0.25rem;
        font-size: 0.875rem;
        max-width: 150px;
        min-width: 80px;
    }
    
    .search-input-group {
        margin-bottom: 1rem;
    }
    
    .pagination {
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.25rem;
    }
    
    .pagination .page-item {
        margin: 0.125rem;
    }
    
    .pagination .page-link {
        padding: 0.375rem 0.5rem;
        font-size: 0.875rem;
    }
    
    .search-input {
        font-size: 16px; /* منع التكبير في iOS */
    }
}
`;

// إضافة الـ CSS للصفحة
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}

